/**
 * Where the sidebar's "Pipeline" entry lands. The entry links to `/pipeline`, a route with no
 * page of its own: its guard loads the org's pipelines and redirects to the board
 * (`/pipelines/:id`) the user was last on. Pure TypeScript with no Vue or router imports so the
 * decision is unit-testable in Vitest's node environment.
 */

/** localStorage key PipelineView writes whenever it loads a pipeline. */
export const LAST_PIPELINE_KEY = 'nerdcrm:lastPipelineId'

/** The two fields the decision reads; the pipelines store's `Pipeline` satisfies it. */
export interface PipelineRef {
  _id: string
  default?: boolean
}

/**
 * Whether this role may open a pipeline board. The board endpoints (`/pipelines/:id/board...`)
 * take member and above; the readonly role gets a 403 there, though it can still list pipelines.
 * An unknown role (session still hydrating) counts as allowed so it is not sent to the list.
 */
export function canOpenPipelineBoard(role: string | null | undefined): boolean {
  return role !== 'readonly'
}

/**
 * The route to open for "Pipeline": the board last viewed if it still exists, else the pipeline
 * flagged default, else the first one; with no pipelines at all, or when the user cannot open a
 * board (`canOpenBoard` false), the pipelines list (where admins can create one).
 */
export function resolvePipelineHome(
  pipelines: readonly PipelineRef[],
  lastId: string | null | undefined,
  canOpenBoard = true,
): string {
  if (!canOpenBoard) return '/pipelines'
  const pick =
    (lastId ? pipelines.find((p) => p._id === lastId) : undefined) ??
    pipelines.find((p) => p.default) ??
    pipelines[0]
  return pick ? `/pipelines/${pick._id}` : '/pipelines'
}

/** The saved id, or null when none is saved or storage is unavailable (private window, blocked). */
export function readLastPipelineId(): string | null {
  try {
    return localStorage.getItem(LAST_PIPELINE_KEY)
  } catch {
    return null
  }
}

/** Remember `id` as the last-viewed pipeline. Never throws. */
export function saveLastPipelineId(id: string): void {
  try {
    localStorage.setItem(LAST_PIPELINE_KEY, id)
  } catch {
    // Storage is a convenience only; the default pipeline is the fallback.
  }
}
